import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setSelectedCaratRange, setSelectedCell as setShapeCaratCell, setSelectedShape } from "../../redux/slices/shapeCaratSlice";
import { setSelectedCell as setColorClarityCell } from "../../redux/slices/colorClaritySlice";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { ColorRing } from "react-loader-spinner";
import TablePagination from "@mui/material/TablePagination";
import TableStockAndSales from "../tableStockAndSales";
import AnimatedLoaderText from "./AnimatedLoaderText";
import { mergeFilters, transformFilters } from "../../utils/utils";
import { setIsPreFiltered, setPreDefinedFilter } from "../../redux/slices/pricingSlice";
import { useNavigate } from "react-router-dom";

const colors = ["0000d","0001e","0002f","0003g","0004h","0005i","0006j","0007k","0008l","0009m","0010n","0011o","0012p","0013q","0014r","0015s","0016t","0017u","0018v","0019w","0020x","0021y","0022z"];
const clarities = ["0000fl","0001if","0002vvs1","0003vvs2","0004vs1","0005vs2","0006si1","0007si2","0008si3","0009i1","0010p1","0011i2","0012p2","0013i3","0014p3"];
const SHAPE_LABELS = {
  round: "0000round",
  princess: "0001princess",
  pear: "0002pear",
  oval: "0003oval",
  emerald: "0004emerald",
  marquise: "0005marquise",
  heart: "0006heart",
  cushion: "0007cushion_brilliant",
  cushion_brilliant: "0007cushion_brilliant",
  radiant: "0009radiant",
  "square radiant": "0010square_radiant",
  square_radiant: "0010square_radiant",
  asscher: "0011asscher",
  other: "0064other_shape",
  other_shape: "0064other_shape",
};
const COLOR_LABELS = "DEFGHIJKLMNOPQRSTUVWXYZ".split("");
const CLARITY_LABELS = ["FL","IF","VVS1","VVS2","VS1","VS2","SI1","SI2","SI3","I1","P1","I2","P2","I3","P3"];

// Strict clarity sort order (others come after in A-Z)
const CLARITY_SORT_ORDER = [
  "FL","IF","VVS1","VVS2","VS1","VS2","SI1","SI2","SI3","I1","I2",
];

// Enhanced debounce hook with cleanup
function useDebouncedCallback(callback, delay) {
  const timeoutRef = useRef(null);
  const callbackRef = useRef(callback);
  
  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedCallback;
}

const claritySort = (rowA, rowB, columnId) => {
  const aRaw = (rowA.getValue(columnId) ?? "").toString().toUpperCase();
  const bRaw = (rowB.getValue(columnId) ?? "").toString().toUpperCase();
  const ia = CLARITY_SORT_ORDER.indexOf(aRaw);
  const ib = CLARITY_SORT_ORDER.indexOf(bRaw);

  const big = Number.POSITIVE_INFINITY;
  const ai = ia === -1 ? big : ia;
  const bi = ib === -1 ? big : ib;

  if (ai !== bi) return ai - bi;
  // If both not in strict list, fallback to alphabetical
  if (ai === big && bi === big) return aRaw < bRaw ? -1 : aRaw > bRaw ? 1 : 0;
  return 0;
};

// Sorts carat ranges like "0.23 - 0.29" numerically by start, then end
const caratRangeSort = (rowA, rowB, columnId) => {
  const parseRange = (s) => {
    if (!s) return { from: Number.POSITIVE_INFINITY, to: Number.POSITIVE_INFINITY };
    const parts = s.split("-").map((t) => t.trim());
    const from = parseFloat(parts[0]);
    const to = parseFloat(parts[1]);
    return {
      from: Number.isFinite(from) ? from : Number.POSITIVE_INFINITY,
      to: Number.isFinite(to) ? to : Number.POSITIVE_INFINITY,
    };
  };

  const a = parseRange(rowA.getValue(columnId));
  const b = parseRange(rowB.getValue(columnId));

  if (a.from !== b.from) return a.from < b.from ? -1 : 1;
  if (a.to !== b.to) return a.to < b.to ? -1 : 1;
  return 0;
};

const colorToCode = (lbl) => {
  const upperLbl = (lbl || "").toUpperCase();
  
  // Handle "OTHER" case - return multiple color codes for K–Z
  if (upperLbl === "OTHER") {
    const otherRange = "KLMNOPQRSTUVWXYZ".split("");
    return otherRange.map((color) => {
      const i = COLOR_LABELS.indexOf(color);
      return i >= 0 ? colors[i] : null;
    }).filter(Boolean);
  }
  
  // Regular single color mapping
  const i = COLOR_LABELS.indexOf(upperLbl);
  return i >= 0 ? colors[i] : null;
};
const clarityToCode = (lbl) => {
  const upperLbl = (lbl || "").toUpperCase();
  // Handle "OTHER" case - return multiple clarity codes for SI3–P3
  if (upperLbl === "OTHER") {
    const otherClarityLabels = ["SI3", "I1", "P1", "I2", "P2", "I3", "P3"];
    return otherClarityLabels
      .map((lab) => {
        const i = CLARITY_LABELS.indexOf(lab);
        return i >= 0 ? clarities[i] : null;
      })
      .filter(Boolean);
  }
  const i = CLARITY_LABELS.indexOf(upperLbl);
  return i >= 0 ? clarities[i] : null;
};
const shapeToCode = (lbl) => {
  const k = (lbl || "").toLowerCase().replace(/\s+/g, "_");
  return SHAPE_LABELS[k] || null;
};
const parseCaratRange = (s) => {
  if (!s) return null;
  const [from, to] = s.split("-").map((x) => x.trim());
  return from && to ? { from, to } : null;
};

export const toItemFilter = (pick) => {
  const shapeCode = shapeToCode(pick.shape);
  const colorCode = colorToCode(pick.color);
  const clarityCode = clarityToCode(pick.clarity);
  const carats = parseCaratRange(pick.carat_range);
  
  // Handle color codes - can be single code or array of codes (for "Others")
  const colorFilter = colorCode ? 
    (Array.isArray(colorCode) ? colorCode : [colorCode]) : 
    null;
  // Handle clarity codes - can be single or array (for "OTHER")
  const clarityFilter = clarityCode ?
    (Array.isArray(clarityCode) ? clarityCode : [clarityCode]) :
    null;
  
  return {
    discrete: {
      ...(clarityFilter ? { "diamond#clarity": clarityFilter } : {}),
      ...(colorFilter ? { "diamond#color": colorFilter } : {}),
      ...(shapeCode ? { "diamond#shape": [shapeCode] } : {}),
      ...(carats ? { "gem#carats": [carats] } : {}),
    },
  };
};

export const buildFilterHash = (
  pick,
  { start_date, end_date, is_sale_report = false, size = 1 } = {}
) => ({
  isCsr: true,
  filterHash: {
    size,
    end_date,
    start_date,
    show_mine: true,
    show_others: true,
    is_sale_report,
    seller_account_id: [],
    get_filtered_data: true,
    item_filter: toItemFilter(pick),
    get_only_matching_count: true,
    change_breakdown: true,
  },
  rowFilter: {},
});

/* ====== FILTER FNs ====== */
const filterFns = {
  textIncludesCI: (row, columnId, filterValue) => {
    const normalize = (x) => (x ?? "").toString().trim().toLowerCase();
    const v = normalize(row.getValue(columnId));

    // For multi-select, use exact match against any selected value
    if (Array.isArray(filterValue) && filterValue.length > 0) {
      return filterValue.some((q) => normalize(q) === v);
    }

    // For single value on shape/color/clarity, also use exact match
    const q = normalize(filterValue);
    if (!q) return true;
    if (["shape", "color", "clarity"].includes(columnId)) {
      return v === q;
    }

    // Fallback: substring match (case-insensitive)
    return v.includes(q);
  },
  selectEquals: (row, columnId, filterValue) => {
    const getNorm = (x) => String(x ?? "").trim().toLowerCase();
    const vNorm = getNorm(row.getValue(columnId));

    // Multi-select: ignore blank entries; if nothing left, no filter
    if (Array.isArray(filterValue)) {
      const vals = filterValue.filter((fv) => fv !== "");
      if (vals.length === 0) return true;
      return vals.some((fv) => vNorm === getNorm(fv));
    }

    // Treat blank/undefined as no filter
    if (filterValue == null || filterValue === "") return true;
    return vNorm === getNorm(filterValue);
  },
  numericRange: (row, columnId, filterValue) => {
    const v = row.getValue(columnId);
    if (v == null || v === "") return false;
    const min = filterValue?.min !== "" ? Number(filterValue?.min) : -Infinity;
    const max = filterValue?.max !== "" ? Number(filterValue?.max) : Infinity;
    const num = Number(v);
    return num >= min && num <= max;
  },
  caratRangeOverlap: (row, columnId, filterValue) => {
    const s = row.getValue(columnId);
    if (!s) return true;
    const [fromStr, toStr] = s.split("-").map((t) => t.trim());
    const from = parseFloat(fromStr);
    const to = parseFloat(toStr);
    if (Number.isNaN(from) || Number.isNaN(to)) return true;
    const hasMin = filterValue?.min !== "" && filterValue?.min != null;
    const hasMax = filterValue?.max !== "" && filterValue?.max != null;
    const qMin = hasMin ? parseFloat(filterValue.min) : -Infinity;
    const qMax = hasMax ? parseFloat(filterValue.max) : Infinity;

    if (hasMin && !hasMax) return from >= qMin;
    if (!hasMin && hasMax) return to <= qMax;
    // overlap if row [from,to] intersects query [qMin,qMax]
    return to >= qMin && from <= qMax;
  },
};

const pageSizeOptions = [50, 100, 250];

const columnHelper = createColumnHelper();

// Improved Range Filter Component
const RangeFilter = ({ column }) => {
  const filterValue = column.getFilterValue() ?? { min: "", max: "" };
  const [localMin, setLocalMin] = useState(filterValue.min);
  const [localMax, setLocalMax] = useState(filterValue.max);
  
  // Update local state when filter value changes externally
  useEffect(() => {
    setLocalMin(filterValue.min);
    setLocalMax(filterValue.max);
  }, [filterValue.min, filterValue.max]);
  
  // Debounced update function
  // const debouncedUpdate = useDebouncedCallback((newValue) => {
  //   column.setFilterValue(newValue);
  // }, 500); // 500ms delay
  
  const handleMinChange = (value) => {
    setLocalMin(value);
    // debouncedUpdate({ min: value, max: localMax });
  };
  
  const handleMaxChange = (value) => {
    setLocalMax(value);
    // debouncedUpdate({ min: localMin, max: value });
  };
  function focusSiblingFilterInput(currentEl, toNext = true) {
  const all = Array.from(document.querySelectorAll('[data-filter-input]'));
  const i = all.indexOf(currentEl);
  if (i === -1) return;

  let j = toNext ? i + 1 : i - 1;
  // wrap around within header filters only
  if (j >= all.length) j = 0;
  if (j < 0) j = all.length - 1;

  const target = all[j];
  if (target) target.focus();
}
  
  const handleKeyDown = (e) => {
  if (e.key === 'Enter') {
    commit();
    e.target.blur();
  }
   else if (e.key === "Tab") {
      e.preventDefault();
      focusSiblingFilterInput(e.currentTarget, !e.shiftKey); // next if Tab, prev if Shift+Tab
    }
  
  else if (e.key === 'Escape') {
    setLocalMin("");
    setLocalMax("");
    column.setFilterValue({ min: "", max: "" });
    e.target.blur();
  }
  e.stopPropagation();
};


  const commit = () => column.setFilterValue({ min: localMin, max: localMax });
  return (
    <div
      className="mt-1 flex flex-col gap-1"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        inputMode="decimal"
        placeholder="Min"
        className="w-14 border rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
        value={localMin}
        onChange={(e) => handleMinChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}                 // ✅ commit here
        tabIndex={0}
      />
      <input
        type="text"
        inputMode="decimal"
        placeholder="Max"
        className="w-14 border rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
        value={localMax}
        onChange={(e) => handleMaxChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}   
        tabIndex={0}              // ✅ commit here
      />
    </div>
  );
};

export default function BiAnalysisTable({
    data, 
    isAdmin,
    isMFG,
    showLoadingTable, 
    queryParams}) {
  const dispatch = useDispatch();
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [inventoryFilter, setInventoryFilter] = useState(null);
  const [salesFilter, setSalesFilter] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null); 
  const [openDropdowns, setOpenDropdowns] = useState({});

  // drilldown anchor
  const drilldownRef = useRef(null);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown')) {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const uniq = (arr) => [...new Set(arr.filter(Boolean))];

  // Dropdown option sorters
  const sortColors = useCallback((opts) => {
    const order = ["D","E","F","G","H","I","J","K","L","OTHER","Other"];
    const idx = (v) => {
      const up = String(v).toUpperCase();
      const i = order.indexOf(up);
      return i === -1 ? Number.POSITIVE_INFINITY : i;
    };
    return [...opts].sort((a, b) => {
      const ia = idx(a);
      const ib = idx(b);
      if (ia !== ib) return ia - ib;
      // fallback alphabetical
      const as = String(a);
      const bs = String(b);
      return as < bs ? -1 : as > bs ? 1 : 0;
    });
  }, []);

  const sortClarities = useCallback((opts) => {
    const order = ["FL","IF","VVS1","VVS2","VS1","VS2","SI1","SI2","SI3","I1","I2"]; // as requested
    const idx = (v) => {
      const up = String(v).toUpperCase();
      const i = order.indexOf(up);
      return i === -1 ? Number.POSITIVE_INFINITY : i;
    };
    return [...opts].sort((a, b) => {
      const ia = idx(a);
      const ib = idx(b);
      if (ia !== ib) return ia - ib;
      // fallback alphabetical
      const as = String(a);
      const bs = String(b);
      return as < bs ? -1 : as > bs ? 1 : 0;
    });
  }, []);

  const sortCaratRanges = useCallback((opts) => {
    const parseRange = (s) => {
      if (!s) return { from: Number.POSITIVE_INFINITY, to: Number.POSITIVE_INFINITY };
      const parts = String(s).split("-").map((t) => t.trim());
      const from = parseFloat(parts[0]);
      const to = parseFloat(parts[1]);
      return {
        from: Number.isFinite(from) ? from : Number.POSITIVE_INFINITY,
        to: Number.isFinite(to) ? to : Number.POSITIVE_INFINITY,
      };
    };
    return [...opts].sort((a, b) => {
      const ra = parseRange(a);
      const rb = parseRange(b);
      if (ra.from !== rb.from) return ra.from < rb.from ? -1 : 1;
      if (ra.to !== rb.to) return ra.to < rb.to ? -1 : 1;
      return 0;
    });
  }, []);
  const navigate = useNavigate();

  const handleSearchClick = (value) => {
        if (value.item_filter.discrete["diamond#shape"]?.includes("0007cushion_brilliant")) {
          value.item_filter.discrete["diamond#shape"].push("0008cushion_modified");
        }
        const discreteFilters = mergeFilters(queryParams.filters,transformFilters(value.item_filter));
        dispatch(setIsPreFiltered(true));
        dispatch(setPreDefinedFilter(discreteFilters));
        navigate(`/price_co-pilot?page=0`);
      };

  const shapeOpts = useMemo(() => uniq(data?.map((d) => d.shape) || []), [data]);
  const colorOpts = useMemo(() => sortColors(uniq(data?.map((d) => d.color) || [])), [data, sortColors]);
  const clarityOpts = useMemo(() => sortClarities(uniq(data?.map((d) => d.clarity) || [])), [data, sortClarities]);
  const caratRangeOpts = useMemo(() => sortCaratRanges(uniq(data?.map((d) => d.carat_range) || [])), [data, sortCaratRanges]);
  const priorityOpts = ["High", "Low"];
  const needSurplusOpts = ["Needs", "Surplus"]; 

  // ===== Heatmap helpers =====
  const numericExtent = (arr, key) => {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const item of arr || []) {
      const v = Number(item?.[key]);
      if (!Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (min === Number.POSITIVE_INFINITY) min = 0;
    if (max === Number.NEGATIVE_INFINITY) max = 0;
    return { min, max };
  };

  // Compute min/max for heatmap columns
  const heatExtents = useMemo(() => ({
    inv_aging: numericExtent(data, "inv_aging"),
    sold_aging: numericExtent(data, "sold_aging"),
    avg_aging: numericExtent(data, "avg_aging"),
    needs: numericExtent(data, "needs"),
  }), [data]);

  // Map value to 3-way color scale: green (low) -> yellow (mid) -> red (high)
  const valueToHeatColor = (value, min, max, reverse = false) => {
    const v = Number(value);
    if (!Number.isFinite(v) || !Number.isFinite(min) || !Number.isFinite(max) || min === max) {
      return "#FFF9C4"; // neutral light yellow when no range
    }
    let t = (v - min) / (max - min);
    t = Math.min(1, Math.max(0, t));
    if (reverse) t = 1 - t;
    // Interpolate hue from 120 (green) -> 60 (yellow) -> 0 (red)
    let hue;
    if (t <= 0.5) {
      const tt = t / 0.5;
      hue = 120 + (60 - 120) * tt; // 120 -> 60
    } else {
      const tt = (t - 0.5) / 0.5;
      hue = 60 + (0 - 60) * tt; // 60 -> 0
    }
    return `hsl(${hue}, 85%, 85%)`;
  };


  // ...existing code...
// const columns = [
//   columnHelper.accessor("shape", {
//     header: "Shape",
//     filterFn: "textIncludesCI",
//     meta: { filterVariant: "select", options: shapeOpts, width: 110 },
//   }),
//   columnHelper.accessor("carat_range", {
//     header: "Carat Range",
//     filterFn: "textIncludesCI",
//     sortingFn: caratRangeSort,
//     meta: { filterVariant: "select", options: caratRangeOpts, width: 120 },
//   }),
//   columnHelper.accessor("color", {
//     header: "Color",
//     filterFn: "textIncludesCI",
//     meta: { filterVariant: "select", options: colorOpts, width: 90 },
//   }),
//   columnHelper.accessor("clarity", {
//     header: "Clarity",
//     filterFn: "textIncludesCI",
//     sortingFn: claritySort,
//     meta: { filterVariant: "select", options: clarityOpts, width: 100 },
//   }),
//   // ...repeat for other columns, adjusting width as needed...
// ];
// ...existing code...

  const columns = [
    columnHelper.accessor("shape", {
      header: "Shape",
      filterFn: "textIncludesCI",
      meta: { filterVariant: "select", options: shapeOpts, width: 80 },
    }),
    columnHelper.accessor("carat_range", {
      header: "Carat Range",
      filterFn: "textIncludesCI",
      sortingFn: caratRangeSort,
      meta: { filterVariant: "select", options: caratRangeOpts,  },
    }),
    columnHelper.accessor("color", {
      header: "Color",
      filterFn: "textIncludesCI",
      meta: { filterVariant: "select", options: colorOpts, width: 80 },
    }),
    columnHelper.accessor("clarity", {
      header: "Clarity",
      filterFn: "textIncludesCI",
      sortingFn: claritySort,
      meta: { filterVariant: "select", options: clarityOpts, width: 110 },
    }),
    columnHelper.accessor("inv_count", { 
      header: "Inv Count", 
      filterFn: "numericRange", 
      meta: { isNumeric: true, filterVariant: "range" , width: 110} 
    }),
    columnHelper.accessor("inv_value", { 
      header: "Inv Value", 
      filterFn: "numericRange", 
      cell: (info) => {
        return (
          <p  style={{ pointerEvents: "none" }}>
            ${info.getValue()}
          </p>
        );
      },
      meta: { isNumeric: true, filterVariant: "range" } 
    }),
    columnHelper.accessor("inv_aging", { 
      header: "Inv Aging", 
      filterFn: "numericRange",
      cell: (info) => info.getValue(),
      meta: { isNumeric: true, filterVariant: "range" } 
    }),
    columnHelper.accessor("sold_count", { 
      header: "Sold Count", 
      filterFn: "numericRange", 
      meta: { isNumeric: true, filterVariant: "range" } 
    }),
    columnHelper.accessor("sold_value", { 
      header: "Sold Value", 
      filterFn: "numericRange", 
      cell: (info) => {
        return (
          <p  style={{ pointerEvents: "none" }}>
            ${info.getValue()}
          </p>
        );
      },
      meta: { isNumeric: true, filterVariant: "range" } 
    }),
    columnHelper.accessor("sold_aging", { 
      header: "Sold Aging", 
      filterFn: "numericRange",
      cell: (info) => info.getValue(),
      meta: { isNumeric: true, filterVariant: "range" } 
    }),
    columnHelper.accessor("avg_aging", { 
      header: "Avg Aging", 
      filterFn: "numericRange",
      cell: (info) => info.getValue(),
      meta: { isNumeric: true, filterVariant: "range" } 
    }),
    columnHelper.accessor("needs", { 
      header: "Needs", 
      filterFn: "numericRange",
      cell: (info) => info.getValue(),
      meta: { isNumeric: true, filterVariant: "range" } 
    }),
    columnHelper.accessor("turn", { 
      header: "Turn", 
      filterFn: "numericRange", 
      meta: { isNumeric: true, filterVariant: "range", width: 90 } 
    }),
    columnHelper.accessor("overhang", { 
      header: "Overhang", 
      filterFn: "numericRange", 
      meta: { isNumeric: true, filterVariant: "range" } 
    }),
    columnHelper.accessor("needs_surplus", {
      header: "Needs/Surplus",
      filterFn: "selectEquals",
      cell: (info) => {
        const needsSurplus = info.getValue();
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              needsSurplus === "Needs" ? "bg-green-100 text-green-800" : 
              (needsSurplus === "Surplus" ? "bg-red-100 text-red-800" : "")
            }`}
             style={{ pointerEvents: "none" }}
          >
            {needsSurplus}
          </span>
        );
      },
      meta: { filterVariant: "selectFixed", options: needSurplusOpts, width: 130 },
    }),
    columnHelper.accessor("priority", {
      header: "Priority",
      filterFn: "selectEquals",
      cell: (info) => {
        const priority = info.getValue();
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              priority === "High" ? "bg-green-100 text-green-800" : 
              (priority === "Low" ? "bg-red-100 text-red-800" : "")
            }`}
             style={{ pointerEvents: "none" }}
          >
            {priority}
          </span>
        );
      },
      meta: { filterVariant: "selectFixed", options: priorityOpts },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns,
    initialState: {
      pagination: {
        pageSize: pageSizeOptions[0], // default 50
      },
    },
  });

  function handleRowClick(row) {
    const picked = (({ shape, carat_range, color, clarity }) => ({
      shape,
      carat_range,
      color,
      clarity,
    }))(row.original ?? {});
    // console.log("picked", picked);

    const stockPayload = buildFilterHash(picked, {
      start_date: queryParams.startDate,
      end_date: queryParams.endDate,
      is_sale_report: false,
    });

    const salesPayload = buildFilterHash(picked, {
      start_date: queryParams.startDate,
      end_date: queryParams.endDate,
      is_sale_report: true,
    });
    
    const sArr = stockPayload.filterHash.item_filter.discrete["diamond#shape"];
    if (Array.isArray(sArr) && sArr.includes("0007cushion_brilliant") && !sArr.includes("0008cushion_modified")) {
      sArr.push("0008cushion_modified");
    }
    const saArr = salesPayload.filterHash.item_filter.discrete["diamond#shape"];
    if (Array.isArray(saArr) && saArr.includes("0007cushion_brilliant") && !saArr.includes("0008cushion_modified")) {
      saArr.push("0008cushion_modified");
    }

    const url = new URL(window.location);
    const shCode = shapeToCode(picked.shape) || picked.shape;
    const colorCodeResult = colorToCode(picked.color);
    // For URL: when OTHER, send CSV of K–Z color codes; else send single code/label
    const colParam = (picked.color && picked.color.toUpperCase() === "OTHER" && Array.isArray(colorCodeResult))
      ? colorCodeResult.join(",")
      : (colorCodeResult || picked.color);
    const clrCodeResult = clarityToCode(picked.clarity);
    // For URL: when OTHER, send CSV of SI3–P3 clarity codes; else send single code/label
    const clrParam = (picked.clarity && picked.clarity.toUpperCase() === "OTHER" && Array.isArray(clrCodeResult))
      ? clrCodeResult.join(",")
      : (clrCodeResult || picked.clarity);
    const crNorm = (picked.carat_range || "").replace(/\s+/g, "");
    url.searchParams.set("sh", shCode);
    url.searchParams.set("cr", crNorm);
    // Ensure no stray leading 'C' before the first code
    const colParamClean = typeof colParam === "string" && colParam.startsWith("C") ? colParam.slice(1) : colParam;
    url.searchParams.set("col", colParamClean);
    url.searchParams.set("clr", clrParam);
    window.history.replaceState({}, "", url);

    // Also update Redux so grid view reflects the latest selection immediately
    dispatch(setSelectedShape(shCode));
    dispatch(setSelectedCaratRange(crNorm));
    dispatch(setShapeCaratCell({ shape: shCode, caratRange: crNorm }));
    const reduxClr = (picked.clarity && picked.clarity.toUpperCase() === "OTHER") ? picked.clarity : (clrCodeResult || picked.clarity);
    dispatch(setColorClarityCell({ color: (picked.color && picked.color.toUpperCase() === "OTHER") ? picked.color : (colorCodeResult || picked.color), clarity: reduxClr }));

    // Save to local state for drilldown
    const key = `${picked.shape}|${picked.color}|${picked.clarity}|${(picked.carat_range || "").replace(/\s+/g, "")}`;
    setSelectedKey(key);
    setInventoryFilter(structuredClone(stockPayload));
    setSalesFilter(structuredClone(salesPayload));

    setTimeout(() => drilldownRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  }

  // Per-column filter UI
  const ColumnFilter = ({ column }) => {
    // Link column has filtering disabled
    if (!column.getCanFilter()) return null;
    const variant = column.columnDef.meta?.filterVariant;

    if (variant === "range") {
      return <RangeFilter column={column} />;
    }

    if (variant === "select" || variant === "selectFixed") {
      const options = column.columnDef.meta?.options || [];
      const value = column.getFilterValue() ?? "";
      
      // Check if this is a multi-select column
      const isMultiSelect = ["shape", "color", "clarity", "carat_range", "needs_surplus", "priority"].includes(column.id);
      
      if (isMultiSelect) {
        // Multi-select implementation with checkbox dropdown
        const selectedValues = Array.isArray(value) ? value : [];
        const isDropdownOpen = openDropdowns[column.id] || false;
        
        return (
          <div className="mt-1 relative filter-dropdown">
            <button
              type="button"
              onClick={() => setOpenDropdowns(prev => ({
                ...prev,
                [column.id]: !prev[column.id]
              }))}
              className="w-14 border rounded px-1 py-0.5 text-xs bg-white hover:bg-gray-50 flex items-center justify-between"
            >
              <span className="truncate">
                {selectedValues.length === 0 ? "All" : `${selectedValues.length}`}
              </span>
              <span className="ml-1 text-[10px]">▼</span>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-max bg-white border rounded shadow-lg z-10 max-h-60 overflow-y-auto filter-dropdown">
                <div className="p-2 border-b sticky top-0 bg-white">
                  <button
                    type="button"
                    onClick={() => {
                      column.setFilterValue([]);
                      setOpenDropdowns(prev => ({
                        ...prev,
                        [column.id]: false
                      }));
                    }}
                    className="w-full text-xs text-blue-500 hover:text-blue-700 underline"
                  >
                    Clear all
                  </button>
                </div>
                <div className="p-1">
                  {options.map((opt) => {
                    const isChecked = selectedValues.includes(opt);
                    return (
                      <label key={String(opt || "blank")} className="flex items-center gap-2 p-1 hover:bg-gray-50 cursor-pointer text-xs">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              column.setFilterValue([...selectedValues, opt]);
                            } else {
                              column.setFilterValue(selectedValues.filter(v => v !== opt));
                            }
                          }}
                          className="w-3 h-3"
                        />
                        <span className="truncate">{opt === "" ? "(blank)" : opt}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      }
      
      // Single select for other columns
      const SENTINEL_ALL = "__ALL__";
      const SENTINEL_BLANK = "__BLANK__";
      const rawFilterValue = column.getFilterValue();
      const uiValue = rawFilterValue == null ? SENTINEL_ALL : (rawFilterValue === "" ? SENTINEL_BLANK : rawFilterValue);
      
      return (
        <select
          className="mt-1 w-14 border rounded px-1 py-0.5 text-xs"
          value={uiValue}
          onChange={(e) => {
            const sel = e.target.value;
            if (sel === SENTINEL_ALL) {
              column.setFilterValue(undefined);
            } else if (sel === SENTINEL_BLANK) {
              column.setFilterValue("");
            } else {
              column.setFilterValue(sel);
            }
          }}
        >
          <option value={SENTINEL_ALL}>All</option>
          {options.map((opt) => (
            <option key={String(opt || "blank")} value={opt === "" ? SENTINEL_BLANK : opt}>
              {opt === "" ? "(blank)" : opt}
            </option>
          ))}
        </select>
      );
    }
    
    // default text contains
    const value = column.getFilterValue() ?? "";
    return (
      <input
        type="text"
        placeholder="filter…"
        className="mt-1 w-full border rounded px-1 py-0.5 text-xs"
        value={value}
        onChange={(e) => column.setFilterValue(e.target.value)}
      />
    );
  };
     
  return (
    !showLoadingTable ?(
    
    <div className="p-4 w-full  flex flex-col justify-center">
    <div className="w-full flex flex-col ">
        <div className="w-full max-h-[100vh] overflow-y-scroll overflow-x-auto">
      <table className=" border min-h-[30vh] mx-auto border-gray-300">
        <thead className="bg-gray-100 sticky top-0 z-0">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  className="px-2 text-center py-1 border align-top"
                  onClick={
                    header.column.getCanSort()
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                  style={{
                    cursor: header.column.getCanSort() ? "pointer" : "default",
                    width: header.column.columnDef.meta?.width || 110, // set default/fixed width
                    minWidth: header.column.columnDef.meta?.width || 110,
                    maxWidth: header.column.columnDef.meta?.width || 110,
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  <div className="flex flex-col items-stretch gap-1">
                    <div
                      className="select-none mx-auto text-xs flex items-center justify-center gap-1"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      <span>
                        {{ asc: "🔼", desc: "🔽" }[header.column.getIsSorted()] ?? null}
                      </span>
                    </div>
                    {/* Per-column filter (except Link) */}
                    {header.column.columnDef.enableColumnFilter !== false && (
                      <div className="mx-auto"
                       onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                      <ColumnFilter column={header.column} />

                      </div>  
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-gray-500 text-base">
                No matching category found
              </td>
            </tr>
          ) : (
          table.getRowModel().rows.map((row) => {
            const key = `${row.original.shape}|${row.original.color}|${row.original.clarity}|${(row.original.carat_range || "").replace(/\s+/g, "")}`;
            const isSelected = selectedKey === key;
            return (
              <tr
                key={row.id}
                className={
                  " hover:bg-gray-50 cursor-pointer " +
                  (isSelected ? " bg-[#E4F0FF] " : "bg-white")
                }
                onClick={() => {
                  setSelectedKey(key);
                  handleRowClick(row);
                }}
                role="button"
                tabIndex={0}
              >
                {row.getVisibleCells().map((cell) => {
                  const isNumeric = cell.column.columnDef.meta?.isNumeric;
                  // Determine heatmap background for specific columns
                  let bgStyle = undefined;
                  const columnId = cell.column.id;
                  const rawValue = cell.getValue();
                  if (columnId === 'inv_aging') {
                    const { min, max } = heatExtents.inv_aging;
                    bgStyle = { backgroundColor: valueToHeatColor(rawValue, min, max, false) };
                  } else if (columnId === 'sold_aging') {
                    const { min, max } = heatExtents.sold_aging;
                    bgStyle = { backgroundColor: valueToHeatColor(rawValue, min, max, false) };
                  } else if (columnId === 'avg_aging') {
                    const { min, max } = heatExtents.avg_aging;
                    bgStyle = { backgroundColor: valueToHeatColor(rawValue, min, max, false) };
                  } else if (columnId === 'needs') {
                    const { min, max } = heatExtents.needs;
                    bgStyle = { backgroundColor: valueToHeatColor(rawValue, min, max, true) };
                  }
                  return (
                    <td
                      key={cell.id}
                      className={`px-3 py-2 border text-xs font-medium ${
                        isNumeric ? "text-right" : ""
                      }`}
                      style={bgStyle}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })
        )}
      </tbody>
      </table>
      
      </div>
      <div className="min-h-[46px] mx-auto max-h-[46px] w-full max-w-[1701px]  bg-white border border-solid border-[#EAEAEA]">
        <TablePagination
          component="div"
          count={table.getFilteredRowModel().rows.length}
          page={table.getState().pagination.pageIndex}
          onPageChange={(_, newPage) => table.setPageIndex(newPage)}
          rowsPerPage={table.getState().pagination.pageSize}
          onRowsPerPageChange={(e) => {
            table.setPageSize(parseInt(e.target.value, 10));
            table.setPageIndex(0);
          }}
          rowsPerPageOptions={pageSizeOptions}
        />
      </div>
    </div>
    
     
      {inventoryFilter && salesFilter && (
        <TableStockAndSales
          key={selectedKey}
          isAdmin={isAdmin}
          isMFG={isMFG}
          drilldownRef={drilldownRef}
          isCsr={true}
          inventoryFilter={inventoryFilter}
          salesFilter={salesFilter}
          shapeOpts={shapeOpts}
          colorOpts={colorOpts}
          clarityOpts={clarityOpts}
          caratRangeOpts={caratRangeOpts}
          handleSearchClick={handleSearchClick}
          onFilterChange={(filterInfo) => {
            // Handle filter changes from the dropdowns
            // console.log('Filter changed:', filterInfo);
            // You can add logic here to update the filters if needed
          }}
        />
      )}
      </div> 
    
    ) :
    (
        <div className="flex flex-col justify-center items-center h-20">
        <ColorRing
            visible={true}
            height="60"
            width="60"
            ariaLabel="blocks-loading"
            colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
        />
        <AnimatedLoaderText />
        </div>
    )
  );
}
